// BHP-1063: add/edit form for a DNS record. Client-side validation mirrors the
// backend rules so the user hears about a bad value before the round trip.
// BHP-1048: a server 422 lands on the field it belongs to — the property paths
// in CreateDnsRecordDto match these input names. Anything the backend rejects
// that this form does not render stays in the form-level banner.
import { useState } from 'react';
import {
  DNS_RECORD_TYPES,
  type DnsRecordErrors,
  type DnsRecordInput,
  hasErrors,
  isReadOnlyType,
  requiresPriority,
  TTL_DEFAULT,
  validateRecord
} from '../dns';

const EMPTY: DnsRecordInput = {
  type: 'A',
  name: '',
  value: '',
  priority: '0',
  ttl: TTL_DEFAULT
};

const FIELD =
  'w-full rounded-md border border-solid border-slate-300 px-3 py-1.5 text-sm text-slate-800 focus:border-teal-500 focus:outline-none';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600';

function FieldError({ message }: { message?: string }): JSX.Element | null {
  if (message === undefined) {
    return null;
  }
  return <p className="mt-1 text-xs text-rose-600">{message}</p>;
}

export default function DnsRecordForm({
  initial,
  heading,
  submitLabel,
  isSubmitting,
  serverError,
  serverFieldErrors,
  onSubmit,
  onCancel
}: {
  initial?: DnsRecordInput;
  heading: string;
  submitLabel: string;
  isSubmitting: boolean;
  serverError: string | null;
  /** BHP-1048: 422 violations the backend bound to a field of this form. */
  serverFieldErrors?: Record<string, string>;
  onSubmit: (input: DnsRecordInput) => void;
  onCancel: () => void;
}): JSX.Element {
  const [input, setInput] = useState<DnsRecordInput>(initial ?? EMPTY);
  const [errors, setErrors] = useState<DnsRecordErrors>({});

  // Client-side validation runs on every submit and therefore wins: after a
  // server 422 the user edits and resubmits, and the fresh local verdict should
  // replace the message from the previous round trip.
  const shown: Record<string, string | undefined> = {
    ...serverFieldErrors,
    ...errors
  };

  const set = <K extends keyof DnsRecordInput>(
    key: K,
    value: DnsRecordInput[K]
  ): void => {
    setInput((current) => ({ ...current, [key]: value }));
  };

  const submit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const found = validateRecord(input);
    setErrors(found);
    if (!hasErrors(found)) {
      onSubmit(input);
    }
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      aria-label={heading}
      className="rounded-xl border border-solid border-slate-200 bg-slate-50 p-4"
    >
      <h4 className="mb-3 text-sm font-semibold text-slate-800">{heading}</h4>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={LABEL} htmlFor="dns-type">
            Type
          </label>
          <select
            id="dns-type"
            value={input.type}
            onChange={(event) => {
              set('type', event.currentTarget.value);
            }}
            className={FIELD}
          >
            {DNS_RECORD_TYPES.filter((type) => !isReadOnlyType(type)).map(
              (type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              )
            )}
          </select>
          <FieldError message={shown.type} />
        </div>

        <div>
          <label className={LABEL} htmlFor="dns-name">
            Name
          </label>
          <input
            id="dns-name"
            value={input.name}
            onChange={(event) => {
              set('name', event.currentTarget.value);
            }}
            placeholder="@ or www"
            className={FIELD}
          />
          <FieldError message={shown.name} />
        </div>

        <div className="sm:col-span-2">
          <label className={LABEL} htmlFor="dns-value">
            Value
          </label>
          <input
            id="dns-value"
            value={input.value}
            onChange={(event) => {
              set('value', event.currentTarget.value);
            }}
            className={FIELD}
          />
          <FieldError message={shown.value} />
        </div>

        {requiresPriority(input.type) ? (
          <div>
            <label className={LABEL} htmlFor="dns-priority">
              Priority
            </label>
            <input
              id="dns-priority"
              value={input.priority}
              onChange={(event) => {
                set('priority', event.currentTarget.value);
              }}
              inputMode="numeric"
              className={FIELD}
            />
            <FieldError message={shown.priority} />
          </div>
        ) : null}

        <div>
          <label className={LABEL} htmlFor="dns-ttl">
            TTL (seconds)
          </label>
          <input
            id="dns-ttl"
            type="number"
            value={input.ttl}
            onChange={(event) => {
              set('ttl', Number(event.currentTarget.value));
            }}
            className={FIELD}
          />
          <FieldError message={shown.ttl} />
        </div>
      </div>

      {serverError !== null ? (
        <p
          role="alert"
          className="mt-3 rounded border border-solid border-rose-200 bg-rose-50 p-2 text-sm text-rose-700"
        >
          {serverError}
        </p>
      ) : null}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
